import {
  isWebScalingEnabled,
  subscribeWebScalingConfig,
} from '@/core/services/webScalingConfigService';
import {
  computeDesktopFitScale,
  readViewportMetrics,
  subscribeViewportChanges,
} from '@/core/utils/viewportMetrics';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

const STYLE_ID = 'bloom-global-web-styles';
const SCALE_VAR = '--bloom-desktop-scale';

const DESKTOP_SCALING_CSS = `
  html {
    zoom: var(${SCALE_VAR}, 1);

    /*
      dvh bypasses CSS zoom. Size html with --bloom-100dvh so after zoom it
      matches the visual viewport. Avoid calc(100% / scale) — % height under
      zoom can overshoot and clip nested ScrollViews before content ends.
    */
    --bloom-100dvh: calc(100dvh / var(${SCALE_VAR}, 1));
    height: var(--bloom-100dvh) !important;
    min-height: var(--bloom-100dvh) !important;
    max-height: var(--bloom-100dvh) !important;
  }

  @supports not (zoom: 1) {
    html {
      zoom: normal;
      height: 100% !important;
      min-height: 100% !important;
      max-height: none !important;
      --bloom-100dvh: 100dvh;
    }
    #root {
      transform: scale(var(${SCALE_VAR}, 1));
      transform-origin: top left;
      width: calc(100% / var(${SCALE_VAR}, 1)) !important;
      height: var(--bloom-100dvh, 100%) !important;
      min-height: var(--bloom-100dvh, 100%) !important;
      max-height: var(--bloom-100dvh, 100%) !important;
    }
  }
`;

const BASE_CSS = `
  html {
    --bloom-100dvh: 100dvh;
  }
  html, body {
    width: 100% !important;
    min-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    height: 100% !important;
    max-height: 100% !important;
    overflow: hidden !important;
  }
  #root {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    min-width: 100% !important;
    height: 100% !important;
    max-height: 100% !important;
    min-height: 0 !important;
    overflow-x: hidden !important;
    /* Inner page ScrollViews own vertical scroll; root must not grow past viewport. */
    overflow-y: hidden !important;
    -webkit-overflow-scrolling: touch;
  }

  #home-bloom-ventures-section img {
    position: unset !important;
    left: unset !important;
    top: unset !important;
  }

  .login-dialog-scroll,
  .login-dialog-scroll > div {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }
  .login-dialog-scroll::-webkit-scrollbar,
  .login-dialog-scroll > div::-webkit-scrollbar {
    width: 0 !important;
    height: 0 !important;
    display: none !important;
  }
  .login-dialog-scroll {
    flex-shrink: 1 !important;
    min-height: 0 !important;
    max-height: 100% !important;
    overflow-y: auto !important;
  }
  /* Default: hug content. Full-bleed phone sets inline flexGrow/height. */
  .login-dialog-scroll:not(.login-dialog-scroll-fullscreen) {
    flex-grow: 0 !important;
    height: auto !important;
  }
  .login-dialog-scroll-fullscreen {
    flex-grow: 1 !important;
    height: 100% !important;
  }
`;

function buildGlobalCss(): string {
  return `${BASE_CSS}${isWebScalingEnabled() ? DESKTOP_SCALING_CSS : ''}`;
}

function injectOrUpdateStyle(css: string): void {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

function applyDesktopFitScale(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const metrics = readViewportMetrics();
  const scale = computeDesktopFitScale(metrics);
  document.documentElement.style.setProperty(SCALE_VAR, String(scale));
}

/** Injects web-only global CSS overrides + viewport fit-scale. */
export function GlobalWebStyles() {
  const [configRevision, setConfigRevision] = useState(0);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    return subscribeWebScalingConfig(() => {
      setConfigRevision((revision) => revision + 1);
    });
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    injectOrUpdateStyle(buildGlobalCss());
    applyDesktopFitScale();

    const unsubscribeViewport = subscribeViewportChanges(applyDesktopFitScale);

    return () => {
      unsubscribeViewport();
      document.documentElement.style.removeProperty(SCALE_VAR);
      document.getElementById(STYLE_ID)?.remove();
    };
  }, [configRevision]);

  return null;
}
